import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { useContent } from '../hooks/useContent';

const Cart = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { 
    cartItems, 
    loading, 
    error, 
    total, 
    removeFromCart, 
    proceedToPayment,
    isAuthenticated 
  } = useCart();
  
  const [removingId, setRemovingId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login?redirect=/cart');
    }
  }, [isAuthenticated, navigate]);

  const handleRemove = async (uuid) => {
    setRemovingId(uuid);
    setActionError(null);
    
    const result = await removeFromCart(uuid);
    
    if (!result.success) {
      setActionError(result.message);
    }
    
    setRemovingId(null);
  };

  const handlePayment = async () => {
    if (!isAuthenticated()) {
      navigate('/login?redirect=/cart');
      return;
    }

    setPaymentLoading(true);
    setActionError(null);
    
    const result = await proceedToPayment();
    
    if (!result.success) {
      setActionError(result.message);
      setPaymentLoading(false);
    }
  };

  const { content, loading: contentLoading } = useContent('cart');

  if (!isAuthenticated()) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`pt-16 min-h-screen ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-secondary-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-full border border-primary-500/30 mb-6">
            <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-primary-400 font-medium">{content.breadcrumb}</span>
          </div>
          
          <h1 className={`text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
		  {content.pageTitle}
          </h1>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {(error || actionError) && (
          <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-red-900/20 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error || actionError}</span>
            </div>
          </div>
        )}

        {loading && cartItems.length === 0 && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}

        {!loading && cartItems.length === 0 && (
          <div className={`text-center p-12 rounded-2xl ${isDark ? 'bg-dark-800 border border-dark-700' : 'bg-white shadow-lg'}`}>
            <svg className={`w-24 h-24 mx-auto mb-6 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
		{content.emptyState.title}
            </h2>
            <p className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
		{content.emptyState.description}
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold rounded-xl transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              	{content.emptyState.cta}
            </Link>
          </div>
        )}

        {cartItems.length > 0 && (
          <div className="space-y-6">
            <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800 border border-dark-700' : 'bg-white shadow-lg'}`}>
              <div className={`px-6 py-4 border-b ${isDark ? 'border-dark-700' : 'border-gray-200'}`}>
                <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {cartItems.length} {content.controls.cartItemDesignation}{cartItems.length > 1 ? 's' : ''} dans votre panier
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-dark-700">
                {cartItems.map((item) => (
                  <div 
                    key={item.uuid} 
                    className={`p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${
                      removingId === item.uuid ? 'opacity-50' : ''
                    }`}
                  >
                    <div className={`w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden ${isDark ? 'bg-dark-900' : 'bg-gray-100'}`}>
                      {item.miniature ? (
                        <img 
                          src={`data:image/webp;base64,${item.miniature}`}
                          alt="QR Code"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className={`w-12 h-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.item_type === 'ai' 
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : item.item_type === 'gs1'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {item.item_type === 'ai' ? content.itemBadge.ai : item.item_type === content.itemBadge.gs1 ? 'GS1' : content.itemBadge.classic}
                        </span>
                      </div>
                      
                      <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.redirect_url || content.itemFields.undefinedUrl }
                      </p>
                      
                      {item.prompt && (
                        <p className={`text-xs mt-1 truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
			      {content.promptPrefix}{item.prompt.substring(0, 50)}...
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                      <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.price || 9.99}€
                      </span>
                      
                      <button
                        onClick={() => handleRemove(item.uuid)}
                        disabled={removingId === item.uuid}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark 
                            ? 'hover:bg-red-900/30 text-red-400 hover:text-red-300' 
                            : 'hover:bg-red-50 text-red-500 hover:text-red-600'
                        } disabled:opacity-50`}
                        title={content.controls.removeTitle}
                      >
                        {removingId === item.uuid ? (
                          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-2xl p-6 ${isDark ? 'bg-dark-800 border border-dark-700' : 'bg-white shadow-lg'}`}>
              <div className="flex items-center justify-between mb-6">
                <span className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.controls.total}</span>
                <span className={`text-3xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent`}>
                  {total.toFixed(2)}€
                </span>
              </div>

              <button
                onClick={handlePayment}
                disabled={paymentLoading || cartItems.length === 0}
                className="w-full py-4 px-6 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paymentLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{content.controls.checkoutLoading}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span>{content.controls.checkout}</span>
                  </>
                )}
              </button>

              <p className={`text-center text-sm mt-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
		{content.controls.securePayment}
              </p>
            </div>

            <div className="text-center">
              <Link
                to="/shop"
                className={`inline-flex items-center gap-2 ${isDark ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'} transition-colors`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
		{content.controls.continueShopping}
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Cart;
